import { useState, useRef, useEffect, useCallback, lazy, Suspense } from 'react';
import { AuthProvider, useAuth } from './context/AuthContext';
import { SavedProvider } from './context/SavedContext';
import Header from './components/Header';
import SearchForm from './components/SearchForm';
import ContentDisplay from './components/ContentDisplay';
import AuthGate from './components/AuthGate';
import { saveSession, getSubjectHistory, getRecentHistory, summariseHistory } from './lib/historyService';
import { updateStreak } from './lib/streakService';
import { awardPoints, STREAK_MILESTONE_PTS } from './lib/pointsService';
import { getReactionSummary } from './lib/reactionsService';
import {
  getGuestSearchCount, incrementGuestCount,
  getUserSearchCount, isLimitReached,
} from './lib/searchLimitService';
import { supabase } from './lib/supabase';
import { acceptInvite } from './lib/communityService';

// ── Lazy-loaded panels ────────────────────────────────────────────────────────
const VisionModal         = lazy(() => import('./components/VisionModal'));
const HistoryPanel        = lazy(() => import('./components/HistoryPanel'));
const SavedPanel          = lazy(() => import('./components/SavedPanel'));
const CommunityPanel      = lazy(() => import('./components/CommunityPanel'));
const ProfileModal        = lazy(() => import('./components/ProfileModal'));
const AdminPanel          = lazy(() => import('./components/AdminPanel'));
const PrivacyPolicyModal  = lazy(() => import('./components/PrivacyPolicyModal'));
const TermsOfServiceModal = lazy(() => import('./components/TermsOfServiceModal'));
const PricingModal        = lazy(() => import('./components/PricingModal'));
const PointsDashboard     = lazy(() => import('./components/PointsDashboard'));
const ResetPasswordModal  = lazy(() => import('./components/ResetPasswordModal'));
const BiteFeedPanel       = lazy(() => import('./components/BiteFeedPanel'));
const FaqModal            = lazy(() => import('./components/FaqModal'));
const ExamPanel           = lazy(() => import('./components/ExamPanel'));
const IELTSPanel          = lazy(() => import('./components/IELTSPanel'));
const BiteExamApp         = lazy(() => import('./components/BiteExamApp'));
const JobsPanel           = lazy(() => import('./components/JobsPanel'));

// ── Bite Feed notification helpers ────────────────────────────────────────────

function playBiteFeedChime() {
  try {
    const ctx   = new (window.AudioContext || window.webkitAudioContext)();
    const notes = [523.25, 659.25, 783.99]; // C5, E5, G5
    notes.forEach((freq, i) => {
      const osc  = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type            = 'sine';
      osc.frequency.value = freq;
      const t = ctx.currentTime + i * 0.18;
      gain.gain.setValueAtTime(0, t);
      gain.gain.linearRampToValueAtTime(0.28, t + 0.05);
      gain.gain.exponentialRampToValueAtTime(0.001, t + 0.45);
      osc.start(t);
      osc.stop(t + 0.5);
    });
  } catch { /* audio not supported */ }
}

function speakBiteFeedReady(count) {
  if (!window.speechSynthesis) return;
  const msg = count === 1
    ? 'Your Bite Feed lesson is ready!'
    : `You have ${count} Bite Feed lessons ready!`;
  const utterance  = new SpeechSynthesisUtterance(msg);
  utterance.rate   = 0.92;
  utterance.pitch  = 1.05;
  window.speechSynthesis.cancel(); // avoid overlap with prior speech
  window.speechSynthesis.speak(utterance);
}

// ── Inner app ─────────────────────────────────────────────────────────────────

function AppInner() {
  const { user, isGuest, isAuthenticated, loading, background, learningNotes, userAge, userRole, isAdmin, tier,
          currentStreak, setCurrentStreak, setLongestStreak,
          setTotalPoints, setPointsTier,
          needsPasswordReset } = useAuth();

  const [streakToast,   setStreakToast]   = useState(null);
  const [results,       setResults]       = useState(null);
  const [loadingData,   setLoadingData]   = useState(false);
  const [error,         setError]         = useState(null);
  const [blockedTopic,  setBlockedTopic]  = useState(null);
  const [searchParams,  setSearchParams]  = useState(null);

  const [showVision,    setShowVision]    = useState(false);
  const [showHistory,   setShowHistory]   = useState(false);
  const [showSaved,     setShowSaved]     = useState(false);
  const [showCommunity, setShowCommunity] = useState(false);
  const [showProfile,   setShowProfile]   = useState(false);
  const [showAdmin,     setShowAdmin]     = useState(false);
  const [showPrivacy,   setShowPrivacy]   = useState(false);
  const [showTerms,     setShowTerms]     = useState(false);
  const [showPricing,   setShowPricing]   = useState(false);
  const [showPoints,    setShowPoints]    = useState(false);
  const [showBiteFeed,  setShowBiteFeed]  = useState(false);
  const [showFaq,       setShowFaq]       = useState(false);
  const [showExam,      setShowExam]      = useState(false);
  const [showIELTS,     setShowIELTS]     = useState(false);
  const [showJobs,      setShowJobs]      = useState(false);

  // Open FAQ modal automatically when the user lands on /faq
  useEffect(() => {
    if (window.location.pathname === '/faq') {
      setShowFaq(true);
      window.history.replaceState(null, '', '/faq');
    }
  }, []);

  // Hide Capacitor splash screen only after auth check is done, so we never
  // reveal a black/spinner state. Also add a 7s safety timeout so the splash
  // never gets permanently stuck if auth fails or network is slow.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.Capacitor?.isNativePlatform?.()) return;
    const SplashScreen = window.Capacitor.Plugins.SplashScreen;
    if (!SplashScreen) return;
    // Safety net: always hide after 7 seconds regardless of auth state
    const safety = setTimeout(() => SplashScreen.hide({ fadeOutDuration: 300 }), 7000);
    return () => clearTimeout(safety);
  }, []);

  useEffect(() => {
    if (!loading && typeof window !== 'undefined' && window.Capacitor?.isNativePlatform?.()) {
      const SplashScreen = window.Capacitor.Plugins.SplashScreen;
      if (SplashScreen) {
        setTimeout(() => SplashScreen.hide({ fadeOutDuration: 300 }), 200);
      }
    }
  }, [loading]);

  // ── Android hardware back button ──────────────────────────────────────────
  useEffect(() => {
    if (typeof window === 'undefined' || !window.Capacitor?.isNativePlatform?.()) return;
    const App = window.Capacitor.Plugins.App;
    if (!App) return;
    const listenerResult = App.addListener('backButton', () => {
      if (window.history.length > 1) {
        window.history.back();
      } else {
        App.exitApp?.();
      }
    });
    return () => {
      if (listenerResult && typeof listenerResult.then === 'function') {
        listenerResult.then(l => l?.remove?.(), () => {});
      } else {
        listenerResult?.remove?.();
      }
    };
  }, []);

  const [dailyCount,  setDailyCount]  = useState(0);
  const [feedUnread,  setFeedUnread]  = useState(0); // unread delivered-item count

  useEffect(() => {
    if (user?.id) {
      getUserSearchCount(user.id).then(setDailyCount);
    } else if (isGuest) {
      setDailyCount(getGuestSearchCount());
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, isGuest]);

  // Check for unread Bite Feed items once on login — set badge + browser badge
  useEffect(() => {
    if (!user?.id) {
      setFeedUnread(0);
      if (navigator.clearAppBadge) navigator.clearAppBadge().catch(() => {});
      return;
    }
    const lastSeen = localStorage.getItem(`bfeed_seen_${user.id}`) || '1970-01-01T00:00:00.000Z';
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session) return;
      fetch('/api/bite-feed', { headers: { Authorization: `Bearer ${session.access_token}` } })
        .then(r => r.json())
        .then(({ items }) => {
          const count = (items || []).filter(
            i => i.status === 'delivered' && i.delivered_at && i.delivered_at > lastSeen
          ).length;
          setFeedUnread(count);
          if (count > 0 && navigator.setAppBadge) {
            navigator.setAppBadge(count).catch(() => {});
          }
        })
        .catch(() => {});
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const limitReached = isLimitReached(dailyCount, userRole, tier);

  const handleUrlParams = useCallback(async () => {
    const params      = new URLSearchParams(window.location.search);
    const lessonId    = params.get('lesson');
    const inviteToken = params.get('invite');
    const upgrade     = params.get('upgrade');

    if (upgrade === 'success') {
      window.history.replaceState({}, '', window.location.pathname);
      setTimeout(() => alert('🎉 Upgrade successful! Your new plan is now active.'), 500);
    }

    if (lessonId) {
      const { data } = await supabase
        .from('shared_lessons')
        .select('lesson_data, subject')
        .eq('id', lessonId)
        .single();
      if (data?.lesson_data) {
        setResults(data.lesson_data);
        setSearchParams({ subject: data.subject || 'Shared Lesson', format: 'text' });
      }
      window.history.replaceState({}, '', window.location.pathname);
    }

    if (inviteToken && user?.id) {
      const { data: community } = await acceptInvite({ token: inviteToken, userId: user.id });
      if (community) {
        alert(`✅ You joined "${community.name}"! Open Communities to see it.`);
        setShowCommunity(true);
      }
      window.history.replaceState({}, '', window.location.pathname);
    } else if (inviteToken && !user?.id) {
      sessionStorage.setItem('pendingInvite', inviteToken);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [user]);

  useEffect(() => { handleUrlParams(); }, [handleUrlParams]);

  useEffect(() => {
    const token = sessionStorage.getItem('pendingInvite');
    if (token && user?.id) {
      sessionStorage.removeItem('pendingInvite');
      acceptInvite({ token, userId: user.id }).then(({ data: community }) => {
        if (community) {
          alert(`✅ You joined "${community.name}"!`);
          setShowCommunity(true);
        }
      });
    }
  }, [user]);

  const sessionStartRef = useRef(null);
  const lastSessionRef  = useRef(null);

  const flushSession = () => {
    if (!lastSessionRef.current || !sessionStartRef.current) return;
    const duration = Math.round((Date.now() - sessionStartRef.current) / 1000);
    saveSession({ userId: user?.id, isGuest, ...lastSessionRef.current, durationSeconds: duration });
    lastSessionRef.current  = null;
    sessionStartRef.current = null;
  };

  useEffect(() => {
    const onUnload = () => flushSession();
    window.addEventListener('beforeunload', onUnload);
    return () => { window.removeEventListener('beforeunload', onUnload); flushSession(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isGuest]);

  if (loading) {
    return <div className="auth-loading"><div className="auth-loading-spinner" /></div>;
  }

  if (!isAuthenticated) return <AuthGate />;

  if (needsPasswordReset) {
    return (
      <Suspense fallback={<div className="auth-loading"><div className="auth-loading-spinner" /></div>}>
        <ResetPasswordModal />
      </Suspense>
    );
  }

  // ── Search handler ───────────────────────────────────────────────────────────
  const handleSearch = async ({ subject, aiModel, format, level }) => {
    if (isLimitReached(dailyCount, userRole)) return;

    flushSession();
    setLoadingData(true);
    setError(null);
    setBlockedTopic(null);
    setResults(null);
    setSearchParams({ subject, aiModel, format, level, background });

    try {
      let data;

      if (format === 'text' || format === 'audio') {
        // Both text and audio call generate-text; audio auto-plays TTS in ContentDisplay
        const [{ data: histData }, { data: recentData }, { data: reactionData }] = await Promise.all([
          getSubjectHistory({ userId: user?.id, isGuest, subject, limit: 5 }),
          getRecentHistory({ userId: user?.id, isGuest, limit: 30 }),
          getReactionSummary({ userId: user?.id, isGuest }),
        ]);
        const adaptiveContext  = summariseHistory(histData || []);
        const likedSubjects    = reactionData?.liked    || [];
        const dislikedSubjects = reactionData?.disliked || [];
        // Recent topics the user has studied (excluding current subject) for cross-topic context
        const subjectNorm  = subject.trim().toLowerCase();
        const recentTopics = [...new Set(
          (recentData || []).map(s => s.subject).filter(s => s && s.trim().toLowerCase() !== subjectNorm)
        )].slice(0, 8);

        const res = await fetch('/api/generate-text', {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify({ subject, level, background, learningNotes, age: userAge, aiModel, adaptiveContext, likedSubjects, dislikedSubjects, recentTopics }),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          if (err.blocked) { setBlockedTopic(err.error); setLoadingData(false); return; }
          throw new Error(err.error || 'Failed to generate content');
        }
        data = await res.json();

      } else if (format === 'live') {
        const res = await fetch(`/api/live-search?subject=${encodeURIComponent(subject)}`);
        if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.error || 'Live search failed'); }
        data = await res.json();
      }

      setResults(data);

      // Increment daily count
      if (user?.id) {
        const newCount = await getUserSearchCount(user.id);
        setDailyCount(newCount);
      } else if (isGuest) {
        setDailyCount(incrementGuestCount());
      }

      // Save session
      const { error: saveErr } = await saveSession({ userId: user?.id, isGuest, subject, format, aiModel, level, background, durationSeconds: 0 });
      if (saveErr) console.error('[saveSession] failed:', saveErr);
      sessionStartRef.current = Date.now();
      lastSessionRef.current  = { subject, format, aiModel, level, background };

      // Award points + update streak (text and audio lessons)
      if (user?.id && (format === 'text' || format === 'audio')) {
        const today = new Date().toISOString().split('T')[0];

        const lessonPts = await awardPoints({
          action:      'lesson_complete',
          points:      2,
          description: `Completed a lesson: ${subject}`,
        });
        if (lessonPts?.awarded) {
          setTotalPoints(lessonPts.totalPoints);
          setPointsTier(lessonPts.tier);
        }

        const firstPts = await awardPoints({
          action:      'first_lesson_today',
          points:      5,
          description: 'First lesson of the day bonus',
          dedupKey:    `first_lesson_${today}`,
        });
        if (firstPts?.awarded) {
          setTotalPoints(firstPts.totalPoints);
          setPointsTier(firstPts.tier);
        }

        const streakResult = await updateStreak(user.id);
        if (streakResult) {
          setCurrentStreak(streakResult.streak);
          setLongestStreak(streakResult.longestStreak);

          if (streakResult.milestoneMessage) {
            setStreakToast(streakResult.milestoneMessage);
            setTimeout(() => setStreakToast(null), 5000);

            const milestonePts = STREAK_MILESTONE_PTS[streakResult.streak];
            if (milestonePts) {
              const mResult = await awardPoints({
                action:      `streak_${streakResult.streak}`,
                points:      milestonePts,
                description: `${streakResult.streak}-day learning streak milestone!`,
                dedupKey:    `streak_milestone_${streakResult.streak}`,
              });
              if (mResult?.awarded) {
                setTotalPoints(mResult.totalPoints);
                setPointsTier(mResult.tier);
              }
            }
          }
        }
      }

    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoadingData(false);
    }
  };

  return (
    <div className="app">
      <Header
        onHistoryClick={()   => setShowHistory(true)}
        onSavedClick={()     => setShowSaved(true)}
        onCommunityClick={()  => setShowCommunity(true)}
        onProfileClick={()   => setShowProfile(true)}
        onAdminClick={()     => setShowAdmin(true)}
        onUpgradeClick={()   => setShowPricing(true)}
        onPointsClick={()    => setShowPoints(true)}
        onExamClick={()      => setShowExam(true)}
        onJobsClick={()      => setShowJobs(true)}
        onBiteFeedClick={() => {
          setShowBiteFeed(true);
          if (feedUnread > 0) {
            playBiteFeedChime();
            speakBiteFeedReady(feedUnread);
          }
          if (user?.id) {
            localStorage.setItem(`bfeed_seen_${user.id}`, new Date().toISOString());
            setFeedUnread(0);
            if (navigator.clearAppBadge) navigator.clearAppBadge().catch(() => {});
          }
        }}
        feedUnread={feedUnread}
        currentTier={tier || 'free'}
        currentStreak={currentStreak || 0}
      />

      {streakToast && (
        <div className="streak-toast" role="alert">{streakToast}</div>
      )}

      <main className="main">
        <SearchForm
          onSearch={handleSearch}
          loading={loadingData}
          dailyCount={dailyCount}
          limitReached={limitReached}
          onUpgrade={() => setShowPricing(true)}
          tier={tier}
          userRole={userRole}
        />

        {blockedTopic && (
          <div className="content-blocked-notice">
            <div className="content-blocked-notice__icon">🚫</div>
            <div className="content-blocked-notice__title">Topic Not Supported</div>
            <div className="content-blocked-notice__msg">{blockedTopic} Try a different topic — BiteLearning is here to help you learn!</div>
          </div>
        )}

        {error && (
          <div className="error-card">
            <span className="error-icon">⚠️</span>
            <p>{error}</p>
          </div>
        )}

        {(loadingData || results) && (
          <ContentDisplay
            results={results}
            loading={loadingData}
            searchParams={searchParams}
            onRetryModel={(newModel) => handleSearch({ ...searchParams, aiModel: newModel })}
            onNextLesson={() => handleSearch({ ...searchParams, continuation: true })}
          />
        )}
      </main>

      <footer className="footer">
        <p><strong>BiteLearning</strong> · Powered by <strong>Claude · GPT-4o · Gemini · Perplexity</strong></p>
        <p className="footer-copy">
          © {new Date().getFullYear()} <strong>BiteLearningAI LLC</strong> · All rights reserved ·{' '}
          <button className="footer-vision-link" onClick={() => setShowVision(true)}>Our Vision</button>
          {' · '}
          <button className="footer-vision-link" onClick={() => setShowPrivacy(true)}>Privacy Policy</button>
          {' · '}
          <button className="footer-vision-link" onClick={() => setShowTerms(true)}>Terms of Service</button>
          {' · '}
          <button className="footer-vision-link" onClick={() => setShowFaq(true)}>FAQ</button>
          {' · '}
          <a className="footer-vision-link" href="mailto:info@bitelearning.ai">Contact Us</a>
        </p>
      </footer>

      <Suspense fallback={null}>
        {showVision    && <VisionModal         onClose={() => setShowVision(false)}    />}
        {showHistory   && <HistoryPanel        onClose={() => setShowHistory(false)}   />}
        {showSaved     && <SavedPanel
          onClose={() => setShowSaved(false)}
          onLoadLesson={(lessonData, subject) => {
            setResults(lessonData);
            setSearchParams({ subject, format: 'text' });
            setShowSaved(false);
          }}
        />}
        {showCommunity && <CommunityPanel      onClose={() => setShowCommunity(false)} tier={tier} userRole={userRole} onUpgrade={() => setShowPricing(true)} />}
        {showProfile   && <ProfileModal        onClose={() => setShowProfile(false)}   />}
        {showAdmin && isAdmin && <AdminPanel   onClose={() => setShowAdmin(false)}     />}
        {showPrivacy   && <PrivacyPolicyModal  onClose={() => setShowPrivacy(false)}   />}
        {showTerms     && <TermsOfServiceModal onClose={() => setShowTerms(false)}     />}
        {showPricing   && <PricingModal        onClose={() => setShowPricing(false)}   />}
        {showPoints    && <PointsDashboard     onClose={() => setShowPoints(false)}    />}
        {showFaq       && <FaqModal            onClose={() => setShowFaq(false)}       />}
        {showExam  && !showIELTS && <ExamPanel
          onClose={() => setShowExam(false)}
          onIELTSClick={() => { setShowExam(false); setShowIELTS(true); }}
        />}
        {showIELTS     && <IELTSPanel          onClose={() => setShowIELTS(false)}     />}
        {showJobs      && <JobsPanel           onClose={() => setShowJobs(false)}      />}
        {showBiteFeed  && <BiteFeedPanel
          onClose={() => setShowBiteFeed(false)}
          onLoadLesson={(lessonData, topic) => {
            setResults(lessonData);
            setSearchParams({ subject: topic, format: 'text' });
            setShowBiteFeed(false);
          }}
        />}
      </Suspense>
    </div>
  );
}

export default function App() {
  // Detect exam subdomain — renders a focused exam-only experience
  const isExamSubdomain = window.location.hostname.startsWith('exam.');

  return (
    <AuthProvider>
      <SavedProvider>
        {isExamSubdomain
          ? <Suspense fallback={null}><BiteExamApp /></Suspense>
          : <AppInner />
        }
      </SavedProvider>
    </AuthProvider>
  );
}
